/**
 * TestMessageStep — paso 2 del setup wizard.
 *
 * Enviá un mensaje de prueba desde la nueva cuenta hacia un número
 * personal para verificar que la integración funciona. Después confirmás
 * que lo recibiste y el step queda completo.
 */

import { useState } from "react";
import { Loader2, Send, CheckCircle2, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { whatsappAccountsApi } from "../../api";
import type { WhatsAppAccount } from "../../types";

interface Props {
    account: WhatsAppAccount;
    onComplete: (updated: WhatsAppAccount) => void;
}

const DEFAULT_MESSAGE = "👋 Hola! Este es un mensaje de prueba desde Deenex AI.\n\nSi lo estás leyendo, la integración funciona perfecto. Podés confirmar en la pantalla de configuración.";

export default function TestMessageStep({ account, onComplete }: Props) {
    const [toNumber, setToNumber] = useState("");
    const [message, setMessage] = useState(DEFAULT_MESSAGE);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState<{ to: string; at: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);

    const handleSendTest = async () => {
        if (!toNumber.trim()) {
            setError("Pegá tu número de WhatsApp para recibir la prueba");
            return;
        }
        setSending(true);
        setError(null);
        const res = await whatsappAccountsApi.sendTestMessage(account.id, {
            toNumber: toNumber.trim(),
            message: message.trim(),
        });
        setSending(false);
        if (!res.success) {
            setError(res.error || "Error enviando el test");
            return;
        }
        setSent({ to: res.data.sentTo, at: res.data.sentAt });
    };

    const handleConfirm = async () => {
        setConfirming(true);
        const res = await whatsappAccountsApi.confirmTestReceived(account.id);
        setConfirming(false);
        if (res.success) onComplete(res.data);
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-violet-50 text-violet-600">
                    <MessageCircle size={16} />
                </div>
                <div>
                    <h3 className="text-base font-bold text-slate-900">
                        Mensaje de prueba
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        Enviá un mensaje desde{" "}
                        <span className="font-mono text-slate-700">
                            {account.phoneNumber}
                        </span>{" "}
                        hacia tu número personal para verificar que todo conecta bien.
                    </p>
                </div>
            </div>

            <div className="space-y-4 bg-slate-50/50 border border-slate-100 rounded-xl p-4">
                <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                        Tu número personal (donde recibís el test)
                    </label>
                    <input
                        type="tel"
                        value={toNumber}
                        onChange={(e) => setToNumber(e.target.value)}
                        placeholder="+54 9 11 1234-5678"
                        disabled={!!sent}
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 font-mono focus:outline-none focus:border-green-300 focus:ring-1 focus:ring-green-100 disabled:opacity-60"
                    />
                </div>

                <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                        Mensaje
                    </label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        disabled={!!sent}
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 focus:ring-1 focus:ring-green-100 resize-none disabled:opacity-60"
                    />
                </div>

                {!sent && (
                    <Button
                        variant="primary"
                        onClick={handleSendTest}
                        disabled={sending || !toNumber.trim() || !message.trim()}
                        className="w-full"
                    >
                        {sending ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Enviando…
                            </>
                        ) : (
                            <>
                                <Send size={14} />
                                Enviar mensaje de prueba
                            </>
                        )}
                    </Button>
                )}

                {sent && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="flex items-start gap-2 mb-3">
                            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-emerald-800">
                                    Mensaje enviado a {sent.to}
                                </p>
                                <p className="text-[11px] text-emerald-700 mt-0.5">
                                    {new Date(sent.at).toLocaleTimeString("es-AR")}
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-emerald-800 leading-relaxed">
                            Andá a WhatsApp en tu celular y confirmá que lo recibiste.
                            Después tocá el botón abajo para continuar.
                        </p>
                        {/* Audit D-02: salida si el número estaba mal */}
                        <button
                            onClick={() => { setSent(null); setError(null); }}
                            className="mt-2 text-xs font-medium text-emerald-700 hover:underline"
                        >
                            No me llegó — enviar a otro número
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
                    {error}
                </div>
            )}

            {sent && (
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={confirming}
                    >
                        {confirming ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <CheckCircle2 size={14} />
                        )}
                        Recibí el mensaje
                        <ArrowRight size={14} />
                    </Button>
                </div>
            )}
        </div>
    );
}
